import { useCallback, useEffect, useState } from 'react';
import type { TemplateTaskConfigOverride } from '../types/taskList';
import {
  createTemplateTaskConfigOverride,
  deleteTemplateTaskConfigOverride,
  listTemplateTaskConfigOverrides,
  updateTemplateTaskConfigOverride,
} from '../services/templateTaskConfigService';

type TaskConfigValue = NonNullable<TemplateTaskConfigOverride['taskConfig']>;

interface UseCustomerTaskConfigOverridesResult {
  overrides: TemplateTaskConfigOverride[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (payload: {
    ticketTypeId: string;
    taskConfig: TaskConfigValue;
  }) => Promise<TemplateTaskConfigOverride>;
  update: (
    overrideId: string,
    patch: { taskConfig: TemplateTaskConfigOverride['taskConfig'] },
  ) => Promise<TemplateTaskConfigOverride>;
  remove: (overrideId: string) => Promise<void>;
}

/**
 * Per-customer template-task-config overrides — the task-list analogue of
 * `useCustomerTicketTemplateOverrides`. Lists, creates, updates and
 * soft-deletes the overrides that replace/append the global task lists for
 * a ticket type.
 */
export function useCustomerTaskConfigOverrides(
  customerId: string | null | undefined,
): UseCustomerTaskConfigOverridesResult {
  const [overrides, setOverrides] = useState<TemplateTaskConfigOverride[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!customerId) {
        setOverrides([]);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const items = await listTemplateTaskConfigOverrides(customerId, signal);
        if (signal?.aborted) return;
        setOverrides(items);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setError(
          err instanceof Error ? err.message : 'Failed to load task-config overrides',
        );
      } finally {
        if (!signal?.aborted) setIsLoading(false);
      }
    },
    [customerId],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  const create = useCallback(
    async (payload: { ticketTypeId: string; taskConfig: TaskConfigValue }) => {
      if (!customerId) throw new Error('No customer selected');
      const created = await createTemplateTaskConfigOverride(customerId, payload);
      setOverrides((prev) => [...prev, created]);
      return created;
    },
    [customerId],
  );

  const update = useCallback(
    async (
      overrideId: string,
      patch: { taskConfig: TemplateTaskConfigOverride['taskConfig'] },
    ) => {
      if (!customerId) throw new Error('No customer selected');
      const updated = await updateTemplateTaskConfigOverride(
        customerId,
        overrideId,
        patch,
      );
      setOverrides((prev) =>
        prev.map((o) => (o.id === overrideId ? updated : o)),
      );
      return updated;
    },
    [customerId],
  );

  const remove = useCallback(
    async (overrideId: string) => {
      if (!customerId) throw new Error('No customer selected');
      await deleteTemplateTaskConfigOverride(customerId, overrideId);
      setOverrides((prev) => prev.filter((o) => o.id !== overrideId));
    },
    [customerId],
  );

  return { overrides, isLoading, error, refresh, create, update, remove };
}
